import { useTranslation } from 'react-i18next';
import { useQuotaQuery } from '@entities/Plan';
import { Button } from '@shared/ui';

/** Сколько жетонов можно потратить на игру: дневные и несгораемые; null — квота ещё грузится. */
export function useSpendableTokens(): number | null {
  const { data } = useQuotaQuery();
  return data ? data.remaining + data.bonus : null;
}

interface PlayButtonProps {
  label: string;
  /** Цена старта в жетонах (решение владельца 2026-10-22: история — 2, миссия и бой — по 1). */
  price: number;
  /** Продолжить начатое — бесплатно: цены на кнопке нет. */
  free?: boolean;
  busy?: boolean;
  /** Пока игра готовится (ведущий пишет первую сцену, озвучка): кольцо и эта подпись вместо названия — «Готовим
   *  миссию…» (владелец 2026-10-22: «сделай лоадер современный»). Без неё `busy` просто гасит кнопку. */
  busyLabel?: string;
  onClick: () => void;
  /** Жетонов не хватает — рядом «Тарифы». */
  onPlans?: () => void;
  size?: 'sm';
  /** `outline` — на главной: заметная обводкой, но не оранжевая (оранжевая там одна — «Тренироваться»). */
  variant?: 'ghost' | 'pill-dark' | 'outline';
}

/** Кольцо загрузки: дорожка и вращающаяся дуга; при «меньше движения» стоит. */
function RingSpinner() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeOpacity="0.22" strokeWidth="2.5" />
      <path d="M18 10a8 8 0 0 0-8-8" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Кнопка старта игры с ценой на ней (правило «цена — на кнопке действия»): «Играть · 2 жетона». Жетонов не хватает —
 * неактивная «Жетоны закончились» и «Тарифы»; квота ещё грузится — цену показываем, кнопку не блокируем (решит сервер).
 */
export function PlayButton({ label, price, free, busy, busyLabel, onClick, onPlans, size, variant }: PlayButtonProps) {
  const { t } = useTranslation();
  const spendable = useSpendableTokens();
  const short = !free && price > 0 && spendable !== null && spendable < price;
  return (
    <>
      {busy && busyLabel ? (
        // Готовится — кнопка не гаснет (это не запрет, а ожидание): кольцо и подпись, повторное нажатие ничего не делает.
        <Button size={size} variant={variant} aria-busy className="cursor-progress" aria-live="polite">
          <RingSpinner />
          {busyLabel}
        </Button>
      ) : (
        <Button size={size} variant={variant} onClick={onClick} disabled={busy || short}>
          {short ? t('stories.noTokens') : free || price <= 0 ? label : `${label} · ${t('stories.price', { count: price })}`}
        </Button>
      )}
      {short && onPlans && (
        <Button size={size} variant="ghost" onClick={onPlans}>
          {t('stories.story.toPlans')}
        </Button>
      )}
    </>
  );
}
